/**
 * SlotEditor — week-level override drawer for the Annual Plan: library
 * swap (cascading theme → topic), AI custom-topic build, "no comms this
 * week" blank state, revert and append-only history.
 * Extracted from AnnualPlanView.jsx during the code-quality refactor.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { API } from "../lib/beaconUtils";

export function SlotEditor({ slot, topics, appliedProfile, planId = "default",
                             monthTheme, onClose, onReload, onJumpToGenerate }) {
  // Cascading Theme → Topic, defaulted to the slot's current state.
  const themes = useMemo(() => {
    const map = new Map();
    for (const t of topics) {
      if (!map.has(t.theme_name)) map.set(t.theme_name, []);
      map.get(t.theme_name).push(t);
    }
    return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
  }, [topics]);

  const [themeName, setThemeName] = useState(slot.current.theme_name);
  const [pickedTopic, setPickedTopic] = useState(slot.current.topic_id);
  const [touched, setTouched] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Dynamic-plan / custom-built weeks: the slot's current theme+topic may
  // not exist in the static library that feeds these pickers. Without a
  // fallback the Theme select silently shows the wrong first option and
  // the Topic list is empty. Snap the pickers to a sensible library
  // starting point instead, and only enable Save once the user touches
  // them.
  const currentInLibrary = topics.some((t) => t.id === slot.current.topic_id);
  const themeInLibrary = themes.some((th) => th.name === slot.current.theme_name);
  const monthThemeInLibrary = themes.some((th) => th.name === monthTheme);
  useEffect(() => {
    // The Theme picker defaults to the CALENDAR's month theme so it
    // matches what the user just clicked through from — even when that
    // theme is a dynamic/plan theme not present in the static library
    // (a "Current: … (this plan)" option is prepended for it).
    if (!slot.blank && monthTheme) {
      setThemeName(monthTheme);
      return;
    }
    if (themeInLibrary) return;
    const libTopic = topics.find((t) => t.id === slot.current.topic_id);
    const fallbackTheme =
      libTopic?.theme_name ||
      topics.find((t) => t.month === slot.month)?.theme_name ||
      themes[0]?.name ||
      "";
    setThemeName(fallbackTheme);
    if (!currentInLibrary && slot.blank) {
      // Blank weeks have no real topic to show — snap the picker to the
      // month's first library topic as a starting point. NON-blank custom
      // topics keep their own id: a "Current: …" option is prepended to
      // the Topic select so it reflects what's actually scheduled instead
      // of silently showing the month's W1 topic.
      const firstTopic = topics.find((t) => t.theme_name === fallbackTheme);
      setPickedTopic(firstTopic?.id || "");
    }
    // `slot` object identity churns on every plan reload but the editor
    // is keyed by slot id — the primitive deps below are the real triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeInLibrary, currentInLibrary, topics, themes,
      slot.current.topic_id, slot.month, slot.blank, monthTheme]);

  // Laptop screens: the editor renders below the calendar fold — bring
  // it into view on open so it's never half off-screen.
  const editorRef = useRef(null);
  useEffect(() => {
    editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Build-with-AI (first principles) — one-off custom topic for this slot.
  const [aiBrief, setAiBrief] = useState("");
  const [aiBuilding, setAiBuilding] = useState(false);
  const [aiError, setAiError] = useState(null);

  const topicsForTheme =
    themes.find((th) => th.name === themeName)?.items || [];

  const isDirty =
    touched &&
    Boolean(pickedTopic) &&
    (pickedTopic !== slot.current.topic_id ||
      (slot.overridden && pickedTopic === slot.base.topic_id));

  const onThemeChange = (next) => {
    setTouched(true);
    setThemeName(next);
    const themeRow = themes.find((th) => th.name === next);
    setPickedTopic(themeRow?.items[0]?.id || pickedTopic);
  };

  // Every mutation below is an append-only override write followed by a
  // plan reload — a shared runner keeps the drawer's error handling uniform.
  const runOverride = async (overrideTopicId, fallbackReason, failLabel) => {
    setSaving(true);
    try {
      await axios.post(`${API}/plan/override`, {
        plan_id: planId,
        slot: slot.slot, override_topic_id: overrideTopicId,
        reason: reason.trim() || fallbackReason,
      });
      await onReload();
      onClose();
    } catch (e) {
      alert(`${failLabel}: ${e.message}`);
    } finally { setSaving(false); }
  };

  const saveOverride = () => runOverride(pickedTopic, null, "Override failed");
  const revertToBase = () =>
    runOverride(slot.base.topic_id, "revert to base", "Revert failed");
  const markNoComms = () =>
    runOverride("no-comms", "no comms this week", "Could not blank this week");

  const buildCustomTopic = async () => {
    setAiBuilding(true);
    setAiError(null);
    try {
      await axios.post(`${API}/plan/custom-topic`, {
        plan_id: planId,
        slot: slot.slot,
        brief: aiBrief.trim(),
        reason: reason.trim() || null,
        industry: appliedProfile?.industry || null,
        jurisdictions: appliedProfile?.jurisdictions || [],
        free_text_context: appliedProfile?.free_text_context || null,
      });
      await onReload();
      onClose();
    } catch (e) {
      setAiError(e.response?.data?.detail || e.message);
      setAiBuilding(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/plan/history`, {
        params: { slot: slot.slot, plan_id: planId },
      });
      setHistory(r.data.history);
      setShowHistory(true);
    } catch (e) { alert(`History load failed: ${e.message}`); }
  };

  const topicLookup = Object.fromEntries(topics.map((t) => [t.id, t]));

  return (
    <div className="beacon-slot-editor" data-testid="slot-editor" ref={editorRef}>
      <div className="beacon-slot-editor-head">
        <div>
          <div className="beacon-tiny-label">
            {slot.month.toUpperCase()} · WEEK {slot.week} · THEME:{" "}
            {(monthTheme || slot.current.theme_name).toUpperCase()}
          </div>
          <div className="beacon-slot-editor-title">{slot.current.topic_name}</div>
          {slot.overridden && (
            <div className="beacon-slot-editor-theme">
              <span className="beacon-slot-editor-base">
                base: {slot.base.topic_name} ({slot.base.theme_name})
              </span>
            </div>
          )}
        </div>
        <button type="button" className="beacon-secondary-btn"
          onClick={onClose} data-testid="slot-editor-close">Close</button>
      </div>

      {!currentInLibrary && !slot.blank && topics.length > 0 && (
        <p className="beacon-slot-ai-hint" data-testid="slot-dynamic-note">
          This week&rsquo;s current topic (&ldquo;{slot.current.topic_name}&rdquo;)
          was built for your plan and isn&rsquo;t in the standard library.
          Use &ldquo;Generate pack for this week&rdquo; below to build comms
          for it as-is — or pick a library topic here to replace it.
        </p>
      )}

      <div className="beacon-field">
        <label className="beacon-label">Theme (monthly)</label>
        <select className="beacon-input"
          data-testid="slot-theme-picker"
          value={themeName}
          onChange={(e) => onThemeChange(e.target.value)}>
          {monthTheme && !monthThemeInLibrary && (
            <option value={monthTheme}>
              {`Current: ${monthTheme} (this plan)`}
            </option>
          )}
          {themes.map((th) => (
            <option key={th.name} value={th.name}>{th.name}</option>
          ))}
        </select>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Topic (weekly)</label>
        <select className="beacon-input"
          data-testid="slot-topic-picker"
          value={pickedTopic}
          onChange={(e) => { setTouched(true); setPickedTopic(e.target.value); }}>
          {!currentInLibrary && !slot.blank && (
            <option value={slot.current.topic_id}>
              {`Current: ${slot.current.topic_name} (custom)`}
            </option>
          )}
          {topicsForTheme.map((t) => (
            <option key={t.id} value={t.id}>
              {`${t.month} W${t.week} — ${t.topic_name}`}
            </option>
          ))}
        </select>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Reason for the swap (optional)</label>
        <input className="beacon-input"
          data-testid="slot-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Civil crew refresher week before haul-road works."
          maxLength={240} />
      </div>

      <div className="beacon-slot-ai-build" data-testid="slot-ai-build-section">
        <div className="beacon-tiny-label">OR BUILD A CUSTOM TOPIC WITH AI</div>
        <p className="beacon-slot-ai-hint">
          Describe the topic you want for this week — Claude builds it from
          first principles using your Context Engine profile.
        </p>
        <textarea
          className="beacon-rationale-panel-textarea"
          data-testid="slot-ai-brief"
          value={aiBrief}
          onChange={(e) => setAiBrief(e.target.value)}
          placeholder="e.g. Working near overhead powerlines with EWPs and cranes (max 500 chars)"
          maxLength={500}
          rows={3}
          disabled={aiBuilding}
        />
        {aiError && (
          <div className="beacon-tab-error" data-testid="slot-ai-error">{aiError}</div>
        )}
        <button type="button" className="beacon-secondary-btn"
          data-testid="slot-ai-build"
          disabled={aiBrief.trim().length < 4 || aiBuilding || saving}
          onClick={buildCustomTopic}>
          {aiBuilding ? (
            <><span className="beacon-spinner beacon-spinner-dark beacon-spinner-inline" />
              Claude is building the topic… (15–30s)</>
          ) : (
            <>Build &amp; apply custom topic →</>
          )}
        </button>
      </div>

      <div className="beacon-slot-editor-actions">
        <button type="button" className="beacon-cta beacon-cta-compact"
          data-testid="slot-save-override"
          disabled={!isDirty || saving}
          onClick={saveOverride}>
          {saving ? "Saving…" : "Save override"}
        </button>
        {!slot.blank && (
          <button type="button" className="beacon-secondary-btn beacon-no-comms-btn"
            data-testid="slot-no-comms"
            disabled={saving || aiBuilding}
            title="Deliberately leave this week clear — supports fortnightly toolbox talks."
            onClick={markNoComms}>
            No comms this week
          </button>
        )}
        {slot.overridden && (
          <button type="button" className="beacon-secondary-btn"
            data-testid="slot-revert"
            disabled={saving}
            onClick={revertToBase}>
            Revert to base ({slot.base.topic_name})
          </button>
        )}
        <button type="button" className="beacon-secondary-btn"
          data-testid="slot-jump-generate"
          disabled={slot.blank}
          title={slot.blank ? "This week is deliberately blank" : undefined}
          onClick={() => onJumpToGenerate({
            ...slot.current, month: slot.month, week: slot.week,
          })}>
          Generate pack for this week →
        </button>
        <button type="button" className="beacon-secondary-btn"
          data-testid="slot-show-history"
          onClick={loadHistory}>View override history</button>
      </div>

      {showHistory && (
        <div className="beacon-slot-history" data-testid="slot-history">
          <div className="beacon-tiny-label">
            APPEND-ONLY HISTORY · {history.length} entr{history.length === 1 ? "y" : "ies"}
          </div>
          {history.length === 0 && (
            <div className="beacon-slot-history-empty">
              No overrides yet — this slot still reads the base plan.
            </div>
          )}
          <ul className="beacon-slot-history-list">
            {history.map((h) => {
              const t = topicLookup[h.override_topic_id];
              return (
                <li key={h.id}>
                  <span className="beacon-slot-history-when">
                    {new Date(h.created_at).toLocaleString()}
                  </span>
                  <span className="beacon-slot-history-arrow">→</span>
                  <span className="beacon-slot-history-target">
                    {t ? t.topic_name : h.override_topic_id}
                  </span>
                  {h.reason && (
                    <span className="beacon-slot-history-reason">“{h.reason}”</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
